import React, { useCallback, useEffect, useMemo, useState, Suspense, lazy, memo } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Routes, Route, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AnimatePresence, motion } from 'framer-motion';
import { queryClient } from './lib/queryClient';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { SEO } from './components/shared/SEO';
import { Header } from './components/layout/Header';
import { AnnouncementBar } from './components/layout/AnnouncementBar';
import { MobileDeliverToPill } from './components/layout/MobileDeliverToPill';
import { HeroBanner } from './components/home/HeroBanner';
import { PromoBanners } from './components/home/PromoBanners';
import { ProductSection } from './components/home/ProductSection';
import { Footer } from './components/layout/Footer';
import { MobileAppPromo } from './components/home/MobileAppPromo';
import { MultiBanner } from './components/home/MultiBanner';
import { DoubleBanner } from './components/home/DoubleBanner';
import { MobileFooterNav } from './components/layout/MobileFooterNav';
import { AuthModal } from './components/modals/AuthModal';
import { PrescriptionUpload } from './components/prescription/PrescriptionUpload';
import { InsurancePrescription } from './components/prescription/InsurancePrescription';
import { PharmacistChat } from './components/services/PharmacistChat';
import { QuickRefill } from './components/services/QuickRefill';
import { ShopByCategory } from './components/home/ShopByCategory';
import { TopOffers } from './components/home/TopOffers';
import { BenefitsBar } from './components/home/BenefitsBar';
import { PrescriptionService } from './components/prescription/PrescriptionService';
import { LoadingSpinner } from './components/shared/LoadingSpinner';
import { trackPageView } from './lib/analytics/events';
import { MobileHomeHeader } from './components/MobileHomeHeader';
import { LocationModal } from './components/modals/LocationModal';
import { LazySection } from './components/shared/LazySection';
import { AppDownloadBottomSheet } from './components/modals/AppDownloadBottomSheet';
import { LocationPermissionModal } from './components/modals/LocationPermissionModal';
import { ProductsShowcaseSection } from './components/shared/ProductsShowcaseSection';
import { SectionSkeleton } from './components/shared/SkeletonLoader';
import { CongratulationsModal } from './components/modals/CongratulationsModal';
import { AddressMismatchModal } from './components/modals/AddressMismatchModal';
import { healthProducts, babyProducts } from './data/products';
import { BrandStorePage } from './pages/BrandStorePage';
import { haptics } from './lib/utils/haptics';
import { useStore } from './lib/store';
import { FeatureCardsSection } from './components/home/FeatureCardsSection';
import { PullToRefresh } from './components/shared/PullToRefresh';
// Lazy loaded components with prefetch
const CheckoutPage = lazy(() => import('./pages/CheckoutPage').then(module => ({
  default: module.CheckoutPage
})));
const PersonalizedQuiz = lazy(() => import('./components/services/PersonalizedQuiz').then(module => ({
  default: module.PersonalizedQuiz
})));
const MedicationFinder = lazy(() => import('./components/services/MedicationFinder').then(module => ({
  default: module.MedicationFinder
})));
const DoctorConsultation = lazy(() => import('./components/services/DoctorConsultation').then(module => ({
  default: module.DoctorConsultation
})));
const FlashSales = lazy(() => import('./components/home/FlashSales').then(module => ({
  default: module.FlashSales
})));
const RecommendedProducts = lazy(() => import('./components/home/RecommendedProducts').then(module => ({
  default: module.RecommendedProducts
})));
const TrendingProducts = lazy(() => import('./components/home/TrendingProducts').then(module => ({
  default: module.TrendingProducts
})));
const EnhancedClearanceSection = lazy(() => import('./components/home/EnhancedClearanceSection').then(module => ({
  default: module.EnhancedClearanceSection
})));
const CategorySpecificShowcase = lazy(() => import('./components/home/CategorySpecificShowcase').then(module => ({
  default: module.CategorySpecificShowcase
})));
const BrandsShowcase = lazy(() => import('./components/home/BrandsShowcase').then(module => ({
  default: module.BrandsShowcase
})));
const DealOfTheDay = lazy(() => import('./components/home/DealOfTheDay').then(module => ({
  default: module.DealOfTheDay
})));
const FAQ = lazy(() => import('./components/services/FAQ').then(module => ({
  default: module.FAQ
})));
const detectedDeliveryAddress = '2214 Prince Mohammed Bin Salman Rd, Al Yasmin, Riyadh, Saudi Arabia';
const ThankYouPage = lazy(() => import('./pages/ThankYouPage').then(module => ({
  default: module.ThankYouPage
})));
// Memoized fallback component
const SectionFallback = memo(() => <SectionSkeleton data-id="element-0" data-mp="205ijjb3fz9ot" />);
import { generateOrganizationJsonLd, generateWebsiteJsonLd } from './utils/seo/jsonLd';
export function App() {
  const location = useLocation();
  const {
    language
  } = useStore();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showAddressMismatch, setShowAddressMismatch] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
    address?: string;
    type?: string;
  } | null>(null);
  const [showAppDownload, setShowAppDownload] = useState(false);
  const [showLocationPermission, setShowLocationPermission] = useState(false);
  const [showCongratulationsModal, setShowCongratulationsModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeSection, setActiveSection] = useState('all');
  // Update active section based on scroll position
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-120px 0px -40% 0px',
      threshold: 0
    };
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const sections = ['trending-now', 'flash-sales', 'recommended-products', 'personalized-quiz', 'recently-viewed', 'mother-baby-essentials'];
    sections.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    // Special case for "all" (top of page)
    const handleScroll = () => {
      if (window.scrollY < 100) {
        setActiveSection('all');
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  // Register service worker for offline support
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js').catch(() => {
          // Service worker registration failed
        });
      });
    }
  }, []);
  useEffect(() => {
    trackPageView('home');
  }, []);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.openAuthModal = () => setAuthModalOpen(true);
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.openAuthModal;
      }
    };
  }, []);
  // Check if modals should be shown on first visit
  useEffect(() => {
    const hasSeenAppDownload = localStorage.getItem('hasSeenAppDownload');
    const hasGrantedLocation = localStorage.getItem('hasGrantedLocation');
    // Show location permission modal first (both desktop and mobile)
    if (!hasGrantedLocation) {
      setTimeout(() => {
        setShowLocationPermission(true);
      }, 1000);
    }
    // Show app download bottom sheet only on mobile, after location is handled
    if (!hasSeenAppDownload && window.innerWidth < 768 && hasGrantedLocation) {
      setTimeout(() => {
        setShowAppDownload(true);
      }, 2000);
    }
  }, []);
  const handleLocationModalOpen = useCallback(() => {
    setLocationModalOpen(true);
  }, []);
  const handleLocationModalClose = useCallback(() => {
    setLocationModalOpen(false);
  }, []);
  const handleLocationSelect = useCallback((location: {
    name: string;
    address?: string;
    type?: string;
  }) => {
    setSelectedLocation(location);
    setLocationModalOpen(false);
    const selectedAddress = location.address ?? location.name;
    const isDeliveryAddress = location.type === 'delivery';
    const doesNotMatchCurrentLocation = selectedAddress !== detectedDeliveryAddress;
    if (isDeliveryAddress && doesNotMatchCurrentLocation) {
      window.setTimeout(() => setShowAddressMismatch(true), 180);
    }
  }, []);
  const handleAuthModalClose = useCallback(() => {
    setAuthModalOpen(false);
  }, []);
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);
  const scrollToSection = useCallback((sectionId: string) => {
    if (sectionId === 'all') {
      scrollToTop();
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 120; // Account for fixed header
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, [scrollToTop]);
  const handleAppDownloadClose = () => {
    localStorage.setItem('hasSeenAppDownload', 'true');
    setShowAppDownload(false);
  };
  const handleAppDownloadContinue = () => {
    localStorage.setItem('hasSeenAppDownload', 'true');
    setShowAppDownload(false);
  };
  const handleLocationPermissionClose = () => {
    localStorage.setItem('hasGrantedLocation', 'denied');
    setShowLocationPermission(false);
  };
  const handleLocationPermissionAllow = () => {
    setShowLocationPermission(false);
    // Request browser location permission
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        console.log('Location granted:', position.coords);
        localStorage.setItem('hasGrantedLocation', 'true');
        // Optionally set a default location based on coordinates
        // You could reverse geocode here or set a detected location
        setSelectedLocation({
          name: 'Current Location'
        });
      }, error => {
        console.log('Location denied:', error);
        localStorage.setItem('hasGrantedLocation', 'denied');
        // Show location modal if browser permission was denied
        // so user can manually select location
        setTimeout(() => {
          setLocationModalOpen(true);
        }, 300);
      });
    } else {
      // Geolocation not available, show manual location selector
      localStorage.setItem('hasGrantedLocation', 'denied');
      setTimeout(() => {
        setLocationModalOpen(true);
      }, 300);
    }
  };
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsRefreshing(false);
    window.location.reload();
  };
  const homeJsonLd = [generateOrganizationJsonLd(), generateWebsiteJsonLd()];
  return <ErrorBoundary data-id="element-1" data-mp="2ua58pm7ysk6m">
      <QueryClientProvider client={queryClient} data-id="element-2" data-mp="2uaog48zznq6e">
        <HelmetProvider data-id="element-3" data-mp="2u9dh0mhhn6lm">
          <div dir={language === 'ar' ? 'rtl' : 'ltr'} data-id="element-4" data-mp="2pb22vzk5u9xl">
            <Toaster position="bottom-center" toastOptions={{
            style: {
              background: '#ffffff',
              color: '#1f2937',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '14px 16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              minWidth: '320px'
            },
            className: 'mb-20 md:mb-4',
            success: {
              style: {
                background: '#f0fdf4',
                color: '#166534',
                border: '1px solid #bbf7d0'
              },
              iconTheme: {
                primary: '#22c55e',
                secondary: '#ffffff'
              }
            },
            error: {
              style: {
                background: '#fef2f2',
                color: '#991b1b',
                border: '1px solid #fecaca'
              },
              iconTheme: {
                primary: '#ef4444',
                secondary: '#ffffff'
              }
            },
            loading: {
              style: {
                background: '#eff6ff',
                color: '#1e40af',
                border: '1px solid #bfdbfe'
              },
              iconTheme: {
                primary: '#3b82f6',
                secondary: '#ffffff'
              }
            }
          }} data-id="element-5" data-mp="2pblobq90rggd" />
            <Suspense fallback={<LoadingSpinner data-id="element-7" data-mp="ms5evppdk99t" />} data-id="element-6" data-mp="mrnfwlbbvgy7">
              <AnimatePresence mode="wait" data-id="element-8" data-mp="11nv37ui18yhp">
                <Routes location={location} key={location.pathname} data-id="element-9" data-mp="11omly4qj71qk">
                  <Route path="/" element={<motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-11" data-mp="2d308n69jtb50">
                        <SEO title="Fast Delivery Across Saudi Arabia" description="Shop online at Innova Pharmacy for authentic health and beauty products with fast delivery across Saudi Arabia. Secure checkout, great prices, and 24/7 support." canonical="/en/" hreflangAr="/" hreflangEn="/en/" hreflangDefault="/" keywords="pharmacy Saudi Arabia, online pharmacy, health products, beauty products, medications delivery, vitamins supplements, medical devices, skincare, Riyadh pharmacy, Jeddah pharmacy, fast delivery pharmacy" jsonLd={homeJsonLd} data-id="element-12" data-mp="qm6p08lzlwxf" />
                        <div className="flex flex-col min-h-screen bg-gray-50 overflow-x-hidden" data-id="element-13" data-mp="1yfzxdnsny27q">
                          <div className="hidden md:block" data-id="element-14" data-mp="1tfcbfgjakbkd">
                            <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} showAnnouncement={true} data-id="element-15" data-mp="1ogratg04npve" />
                          </div>
                          <main className="flex-grow overflow-x-hidden pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-16" data-mp="27ekbebna3npm">
                            <h1 className="sr-only" data-id="element-17" data-mp="23cgw1bxhnyz2">
                              Innova Pharmacy - Your Trusted Online Healthcare
                              Store
                            </h1>
                            <div className="md:hidden relative" data-id="element-18" data-mp="2v3mp58eskweu">
                              <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={handleLocationModalOpen} transparentAtTop={true} showExperienceSwitcher data-id="element-19" data-mp="t8fhg89ilm8o" />
                              {/* Mobile Hero Banner connected to transparent header */}
                              <div data-id="element-20" data-mp="ajsf3xiq3ays">
                                <HeroBanner data-id="element-21" data-mp="fh23rayafilk" />
                              </div>
                            </div>

                            {/* Desktop Hero Banner Section - Connected to header */}
                            <div className="hidden md:block" data-id="element-22" data-mp="5hjjetegtusz">
                              <BenefitsBar data-id="element-23" data-mp="5k613lv2lqox" />
                              <div className="container mx-auto px-4 pb-6 pt-5" data-id="element-24" data-mp="1uyc9g4arbq4h">
                                <HeroBanner data-id="element-25" data-mp="1zwvv03lfuzwb" />
                              </div>
                            </div>

                            {/* Sticky Category Filter Pills - Mobile Only */}
                            <div className="md:hidden sticky top-[56px] z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 px-4 py-2.5 overflow-x-auto scrollbar-hide" data-id="element-26" data-mp="3fflc3cwgw1h2">
                              <div className="flex gap-2" data-id="element-27" data-mp="3u614pcr7qbp9">
                                {[{
                            id: 'all',
                            label: 'All',
                            sectionId: 'all'
                          }, {
                            id: 'trending',
                            label: 'Trending',
                            sectionId: 'trending-now'
                          }, {
                            id: 'deals',
                            label: 'Best Deals',
                            sectionId: 'flash-sales'
                          }, {
                            id: 'new',
                            label: 'New Arrivals',
                            sectionId: 'recommended-products'
                          }, {
                            id: 'wellness',
                            label: 'Wellness',
                            sectionId: 'personalized-quiz'
                          }, {
                            id: 'skincare',
                            label: 'Skincare',
                            sectionId: 'recently-viewed'
                          }, {
                            id: 'baby',
                            label: 'Baby Care',
                            sectionId: 'mother-baby-essentials'
                          }].map((cat, idx) => <button key={cat.id} onClick={() => {
                            haptics.selection();
                            scrollToSection(cat.sectionId);
                          }} className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${idx === 0 ? 'bg-[#1E2B55] text-white shadow-md shadow-blue-900/20' : 'bg-gray-100 text-gray-600 active:bg-gray-200'}`} data-id="element-28" data-mp="ip8pq88m7yqk" data-mpi={idx}>
                                    {cat.label}
                                  </button>)}
                              </div>
                            </div>

                            <PullToRefresh onRefresh={handleRefresh} data-id="element-29" data-mp="1keb4hqghf9ip">
                              <div className="flex flex-col py-1 md:py-4" data-id="element-30" data-mp="2c72towjsud7x">
                              <div className="py-1 md:py-4" data-id="element-31" data-mp="2c9fav7hqgjjx">
                                <MultiBanner data-id="element-32" data-mp="279kggtyr1bu4" />
                              </div>
                              <div className="py-1 md:py-4" data-id="element-33" data-mp="22grak2rjnxgg">
                                <ShopByCategory data-id="element-34" data-mp="22f6icjspcqlr" />
                              </div>
                              <div className="py-1 md:py-4" data-id="element-35" data-mp="1xgdfrmeyek0d">
                                <TopOffers data-id="element-36" data-mp="1thw3kogoejc3" />
                              </div>
                              <div className="py-1 md:order-[50] md:py-4" data-id="element-37" data-mp="1oh8ggdvkimn5">
                                <FeatureCardsSection data-id="element-38" data-mp="1jiouwekvzcvb" />
                              </div>
                              <LazySection data-id="element-39" data-mp="1jki0rrnodt29">
                                <div id="flash-sales" className="py-1 md:py-4" data-id="element-40" data-mp="1eknbfsjjtbjb">
                                  <Suspense fallback={<SectionFallback data-id="element-42" data-mp="3dvn04jrqbjbs" />} data-id="element-41" data-mp="3dx7wmexltcca">
                                    <FlashSales data-id="element-43" data-mp="38xlje5hrzw3h" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <LazySection data-id="element-44" data-mp="3hedigl662fzp">
                                <div id="mother-baby-essentials" className="py-1 md:py-4" data-id="element-45" data-mp="3lfp8grsyd38f">
                                  <Suspense fallback={<SectionFallback data-id="element-47" data-mp="3v6a5iasz9zp8" />} data-id="element-46" data-mp="3v5qxblt4qpoe">
                                    <CategorySpecificShowcase title="Mother & Baby Essentials" subtitle="CARING FOR YOUR LITTLE ONES" description="Safe, trusted, and essential products for everyday care of mothers and babies" viewAllLink="#" backgroundColor="bg-gradient-to-r from-pink-50 via-blue-50 to-pink-50" textColor="text-gray-900" iconColor="text-pink-500" products={babyProducts} icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-49" data-mp="3t88rnf4gw8zt">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" data-id="element-50" data-mp="3fgker9me9ghs" />
                                        </svg>} data-id="element-48" data-mp="3v7uy3uvqf8kg" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <div className="md:order-[51]" data-id="element-51" data-mp="1c0qpom9qmnxw">
                                <LazySection data-id="element-52" data-mp="xb17de9ik3ae">
                                  <div id="personalized-quiz" className="py-1 md:py-4" data-id="element-53" data-mp="x86mxmqi2igz">
                                    <Suspense fallback={<SectionFallback data-id="element-55" data-mp="1px41pfvi5x4z" />} data-id="element-54" data-mp="1pzqepv15x4ul">
                                      <PersonalizedQuiz data-id="element-56" data-mp="1uwp11rel47i8" />
                                    </Suspense>
                                  </div>
                                </LazySection>
                              </div>
                              <div className="py-1 md:py-4" data-id="element-57" data-mp="1l1x7qzs1c6t9">
                                <DoubleBanner data-id="element-58" data-mp="5i0qzwtgv1nd" />
                              </div>
                              <div className="py-1 md:py-4" data-id="element-59" data-mp="9l6y16oekwuw">
                                <PromoBanners data-id="element-60" data-mp="9icbn9ltx1yu" />
                              </div>
                              <LazySection data-id="element-61" data-mp="pt4oxh9szar0">
                                <div className="py-1 md:py-4" data-id="element-62" data-mp="l0lbqlf12hmq">
                                  <Suspense fallback={<SectionFallback data-id="element-64" data-mp="g20bilzrzxya" />} data-id="element-63" data-mp="kz0h6vspqyov">
                                    <DealOfTheDay data-id="element-65" data-mp="g2jqpuy1k88m" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <LazySection data-id="element-66" data-mp="18pmnoin2kawr">
                                <div className="py-1 md:py-4" data-id="element-67" data-mp="18ol9i5arb8at">
                                  <Suspense fallback={<SectionFallback data-id="element-69" data-mp="13se36beg7usc" />} data-id="element-68" data-mp="13sw9y1yqlrei">
                                    <BrandsShowcase data-id="element-70" data-mp="yrqfnzpfhw2v" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <LazySection data-id="element-71" data-mp="3jih4sezbq0c8">
                                <div className="py-1 md:py-4" data-id="element-72" data-mp="3jjifujgcamty">
                                  <Suspense fallback={<SectionFallback data-id="element-74" data-mp="3fg2mbvzkh2lh" />} data-id="element-73" data-mp="3fgm5fg0ydx13">
                                    <CategorySpecificShowcase title="Sports Performance Supplements" subtitle="PERFORMANCE BOOSTERS" description="Advanced formulations to enhance your workout intensity, endurance, and recovery" viewAllLink="#" backgroundColor="bg-gradient-to-r from-[#1E2B55] to-[#162041]" textColor="text-white" iconColor="text-[#1E2B55]" products={babyProducts} icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-76" data-mp="2rplx1d9eo56z">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" data-id="element-77" data-mp="20ms26mw3m5c1" />
                                        </svg>} data-id="element-75" data-mp="3fjhzuudle3ks" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <LazySection data-id="element-78" data-mp="2scwgq4x6fy5c">
                                <div id="recommended-products" className="py-1 md:py-4" data-id="element-79" data-mp="2oc2xhouoj7is">
                                  <Suspense fallback={<SectionFallback data-id="element-81" data-mp="2xd10q4xanb0b" />} data-id="element-80" data-mp="2xcj06w3lmaml">
                                    <RecommendedProducts data-id="element-82" data-mp="2xaeldj45vr7j" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <LazySection data-id="element-83" data-mp="3lwvt9kjpzp9k">
                                <div className="py-1 md:py-4" data-id="element-84" data-mp="370dcljsuod32">
                                  <Suspense fallback={<SectionFallback data-id="element-86" data-mp="3bz7v0kmwnzmn" />} data-id="element-85" data-mp="3727ok8m58uv0">
                                    <EnhancedClearanceSection data-id="element-87" data-mp="3bxwuquso5a0a" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <LazySection data-id="element-88" data-mp="32zl1sm4by04o">
                                <div className="py-1 md:py-4" data-id="element-89" data-mp="330cpzbvlkvkx">
                                  <MobileAppPromo data-id="element-90" data-mp="2oayhe5zuii6l" />
                                </div>
                              </LazySection>
                              <div className="py-1 md:py-4" data-id="element-91" data-mp="2t6dq6jizxnw5">
                                <PromoBanners data-id="element-92" data-mp="2fa8k0xuxsjqr" />
                              </div>
                              <LazySection data-id="element-93" data-mp="2jasl2cims7i6">
                                <div id="trending-now" className="py-1 md:py-4" data-id="element-94" data-mp="2jcn2vhylvji1">
                                  <Suspense fallback={<SectionFallback data-id="element-96" data-mp="2znyy36c8cgo8" />} data-id="element-95" data-mp="2zpjl8aw7sxc2">
                                    <TrendingProducts data-id="element-97" data-mp="2us085eb96qlp" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              <div className="py-1 md:py-4" data-id="element-98" data-mp="2ktan5co28q18">
                                <PromoBanners data-id="element-99" data-mp="2kuvktb5o8p3b" />
                              </div>
                              <LazySection data-id="element-100" data-mp="2gsibydvmob2b">
                                <div className="py-1 md:py-4" data-id="element-101" data-mp="2c0qsenaw35lo">
                                  <ProductSection title="Top Selling Products" viewAllLink="#" data-id="element-102" data-mp="2bye5rwu6s72a" />
                                </div>
                              </LazySection>
                              <div className="py-1 md:py-4" data-id="element-103" data-mp="1x2ona8o7mc2b">
                                <div className="container mx-auto px-4" data-id="element-104" data-mp="1t1cww0xihmt2">
                                  <div className="relative aspect-[21/9] overflow-hidden rounded-lg bg-gray-300" data-id="element-105" data-mp="8jkjbvkkjnby">
                                    <div className="absolute inset-0 flex flex-col justify-center p-4 md:p-8 text-gray-800" data-id="element-106" data-mp="ckmkwj1ykbh3">
                                      <span className="text-xs md:text-sm font-bold bg-white text-gray-600 px-2 md:px-3 py-1 rounded-full w-max mb-2 md:mb-4" data-id="element-107" data-mp="hla80tn2g861">
                                        DIMENSIONS
                                      </span>
                                      <h2 className="text-xl md:text-4xl font-bold mb-1 md:mb-2" data-id="element-108" data-mp="3q024ieielmt8">
                                        FULL WIDTH BANNER
                                      </h2>
                                      <p className="text-sm md:text-2xl font-semibold mb-2 md:mb-4" data-id="element-109" data-mp="3kheoc8i3j4i">
                                        ASPECT RATIO 21:9
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <LazySection data-id="element-110" data-mp="2h0yi8cgqqbwc">
                                <div id="recently-viewed" className="py-1 md:py-4" data-id="element-111" data-mp="2lyqaj7las012">
                                  <ProductSection title="Recently Viewed" viewAllLink="#" data-id="element-112" data-mp="281bhztzqzc4v" />
                                </div>
                              </LazySection>
                              <LazySection data-id="element-113" data-mp="23170q1r0mddl">
                                <div className="py-1 md:py-4" data-id="element-114" data-mp="1ob9jk5ri6nxw">
                                  <Suspense fallback={<SectionFallback data-id="element-116" data-mp="1ta2mj495ywjt" />} data-id="element-115" data-mp="1o8ws91zuv186">
                                    <FAQ data-id="element-117" data-mp="1tam4um2q7myd" />
                                  </Suspense>
                                </div>
                              </LazySection>
                              </div>
                            </PullToRefresh>
                          </main>
                          <Footer data-id="element-118" data-mp="10onr3q3twpqn" />
                          <MobileFooterNav data-id="element-119" data-mp="10nusjcf0x6jl" />
                          <PharmacistChat data-id="element-120" data-mp="vp1dhfk397hb" />
                          <div className="h-20 md:hidden" data-id="element-121" data-mp="vq2stw850g4u"></div>
                          <button className="hidden md:block fixed bottom-20 md:bottom-6 right-4 md:right-6 bg-[#1E2B55] text-white p-3 rounded-full shadow-lg hover:bg-[#162041] transition-all active:scale-95" onClick={scrollToTop} aria-label="Scroll to top" data-id="element-122" data-mp="19oj7c9c8raxj">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-123" data-mp="1tca3nnv1b4pn">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" data-id="element-124" data-mp="1xemyhh88ti7n" />
                            </svg>
                          </button>
                          <AuthModal isOpen={authModalOpen} onClose={handleAuthModalClose} data-id="element-125" data-mp="13zubta83ft60" />
                          <LocationModal isOpen={locationModalOpen} onClose={handleLocationModalClose} onSelectLocation={handleLocationSelect} data-id="element-126" data-mp="1dpvsvi214bbf" />
                          <AddressMismatchModal isOpen={showAddressMismatch} selectedAddress={selectedLocation?.address ?? selectedLocation?.name ?? 'Selected address'} detectedLocation={detectedDeliveryAddress} onClose={() => setShowAddressMismatch(false)} onConfirm={() => setShowAddressMismatch(false)} onChangeAddress={() => {
                      setShowAddressMismatch(false);
                      setLocationModalOpen(true);
                    }} data-id="element-127" data-mp="1rqpyjsxozy6e" />
                          <AppDownloadBottomSheet isOpen={showAppDownload} onClose={handleAppDownloadClose} onContinue={handleAppDownloadContinue} data-id="element-128" data-mp="3852jdd1oshg3" />
                          <LocationPermissionModal isOpen={showLocationPermission} onClose={handleLocationPermissionClose} onAllow={handleLocationPermissionAllow} data-id="element-129" data-mp="3vvor137s3zpk" />
                          <CongratulationsModal isOpen={showCongratulationsModal} onClose={() => setShowCongratulationsModal(false)} data-id="element-130" data-mp="2firis2e2ygja" />

                          {/* Unlock Spin Floating Button */}
                          <motion.button onClick={() => setShowCongratulationsModal(true)} className="fixed bottom-24 left-4 z-40 w-16 h-16 rounded-full shadow-[0_0_15px_rgba(236,72,153,0.5)] overflow-hidden border-2 border-white" animate={{
                      y: [0, -12, 0, -6, 0],
                      scale: [1, 1.05, 1, 1.02, 1]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                      repeatDelay: 1.5
                    }} whileHover={{
                      scale: 1.1
                    }} whileTap={{
                      scale: 0.9
                    }} data-id="element-131" data-mp="c2cs4bvmtjbk">
                            <div className="absolute inset-0 bg-[conic-gradient(from_0deg,#f87171,#fbbf24,#34d399,#38bdf8,#60a5fa,#818cf8,#a78bfa,#c084fc,#f472b6,#fb7185)] animate-spin-slow" data-id="element-132" data-mp="2y6br6ofjgt2p"></div>
                            <div className="absolute inset-1 bg-white rounded-full flex flex-col items-center justify-center" data-id="element-133" data-mp="385kgv53dflj9">
                              <span className="text-[10px] font-bold text-gray-900 leading-tight" data-id="element-134" data-mp="3356i6dnntnxw">UNLOCK</span>
                              <span className="text-[10px] font-bold text-gray-900 leading-tight" data-id="element-135" data-mp="2aic1lrtyr8pp">SPIN</span>
                            </div>
                          </motion.button>
                        </div>
                      </motion.div>} data-id="element-10" data-mp="wo8othqgxc7b" />
                  <Route path="/checkout" element={<motion.div initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -20
                }} transition={{
                  duration: 0.2
                }} data-id="element-137" data-mp="1woui271a1vzo">
                        <CheckoutPage data-id="element-138" data-mp="38wj5p12nz5ii" />
                      </motion.div>} data-id="element-136" data-mp="2keoreeme12f6" />
                  <Route path="/thank-you" element={<motion.div initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.95
                }} transition={{
                  duration: 0.2
                }} data-id="element-140" data-mp="3hucorabf42nq">
                        <ThankYouPage data-id="element-141" data-mp="12i3wcvea9y13" />
                      </motion.div>} data-id="element-139" data-mp="3duudz7db97ym" />
                  <Route path="/brand/:brandSlug" element={<motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-143" data-mp="tj08le468ug9">
                        <BrandStorePage data-id="element-144" data-mp="29vxggdtykbm0" />
                      </motion.div>} data-id="element-142" data-mp="ygijha1r1jqw" />
                </Routes>
              </AnimatePresence>
            </Suspense>
          </div>
        </HelmetProvider>
      </QueryClientProvider>
    </ErrorBoundary>;
}